import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, BarChart3, Coins, LayoutDashboard, LogOut, Package, Palette, ShieldAlert,
  ShoppingBag, Truck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ErrorState, Loading } from "@/components/ds";
import { CreatorDashboard } from "@/components/creator/CreatorDashboard";
import { CreatorProducts } from "@/components/creator/CreatorProducts";
import { CreatorSales } from "@/components/creator/CreatorSales";
import { CreatorCommissions } from "@/components/creator/CreatorCommissions";
import { CreatorDeliveries } from "@/components/creator/CreatorDeliveries";
import { CreatorReports } from "@/components/creator/CreatorReports";
import { CreatorCatalogSettings } from "@/components/creator/CreatorCatalogSettings";

const SECTIONS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["produtos", "Produtos", Package],
  ["vendas", "Vendas", ShoppingBag],
  ["comissoes", "Comissões", Coins],
  ["entregas", "Entregas", Truck],
  ["relatorios", "Relatórios", BarChart3],
  ["vitrine", "Personalizar catálogo", Palette],
];

/** Área restrita da criadora: seis seções, tudo somente leitura e só dos próprios dados. */
export default function CreatorPanel() {
  const { logout, impersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [section, setSection] = useState("dashboard");

  const load = async () => {
    setError("");
    try {
      const { data: res } = await api.get("/creator/panel");
      setData(res);
    } catch (e) {
      setError(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Carregando seu painel..." />;

  const current = SECTIONS.find(([key]) => key === section) || SECTIONS[0];

  return (
    <div className="min-h-screen bg-background" data-testid="creator-panel">
      {impersonating && (
        <div
          className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-2 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2.5 sm:px-6"
          data-testid="impersonation-banner"
        >
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-200">
            <ShieldAlert className="h-4 w-4" /> Visualização administrativa — {impersonating}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={stopImpersonation}
            data-testid="exit-impersonation-btn"
            className="h-8 gap-1.5 rounded-full border-amber-500/40 text-[11px]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para Administração
          </Button>
        </div>
      )}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary font-display text-sm font-bold text-primary-foreground">
              T3
            </span>
            <div className="min-w-0">
              <p className="truncate font-display text-base font-semibold leading-none"
                 data-testid="creator-header-name">
                {data.creator.display_name}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Parceira criadora · {data.creator.commission_percent}% de comissão
              </p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="creator-logout-btn"
            className="shrink-0 rounded-full border border-white/10 p-2 text-muted-foreground transition-colors hover:text-destructive"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 pb-2 sm:px-6 lg:hidden"
             data-testid="creator-nav-mobile">
          {SECTIONS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              data-testid={`creator-nav-${key}`}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${
                section === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <div className="mx-auto flex max-w-6xl gap-8 px-4 pb-16 pt-6 sm:px-6">
        <aside className="hidden w-52 shrink-0 lg:block" data-testid="creator-nav-desktop">
          <nav className="sticky top-28 space-y-1">
            {SECTIONS.map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => setSection(key)}
                data-testid={`creator-nav-desktop-${key}`}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                  section === key
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1">
          <h1 className="mb-5 font-display text-2xl font-medium" data-testid="creator-section-title">
            {current[1]}
          </h1>
          {section === "dashboard" && <CreatorDashboard data={data} onGoTo={setSection} />}
          {section === "produtos" && <CreatorProducts data={data} />}
          {section === "vendas" && <CreatorSales data={data} />}
          {section === "comissoes" && <CreatorCommissions data={data} />}
          {section === "entregas" && <CreatorDeliveries data={data} />}
          {section === "relatorios" && <CreatorReports data={data} />}
          {section === "vitrine" && <CreatorCatalogSettings data={data} />}
        </main>
      </div>
    </div>
  );
}
